<template>
  <loading-el v-if="loading"></loading-el>
	<div class="edit-row--single edit-row--b-top mod--edit-bar">
		<div class="section-row section-row--double-colorpicker mb--20">
			<appearance-border
				v-model:enabled="edge.design.border.value"
				:enabled-locked="true"
				:enabled-name="edge.design.border.name"
				v-model:color="edge.design.border.color"
				v-model:width="edge.design.width"
				v-model:style="edge.design.style"
			></appearance-border>
		</div>
		<div class="section-row mb--20">
			<edge-lines
				v-model:start="edge.line.start"
				v-model:start-size="edge.line.startSize"
				v-model:end="edge.line.end"
				v-model:end-size="edge.line.endSize"
			></edge-lines>
		</div>
		<div class="button-wrapper flex--justify-between">
			<div class="button-wrapper">
        <div
          :class="[isUpdateButtonAvailableClassname, hasDesignTemplatesClassname, isTemplateRelatedOccupied]"
          @click="templateUpdate"
          class="button button--plain button--h-25 mr--15"
        >
          <i v-if="!templateRelatedOccupied" class="fas fa-sync mr--15"></i>
          <div
            v-else
            :class="templateRelatedOccupied.colorName"
            class="map-user map-user--small"
          >{{ templateRelatedOccupied.initials }}</div>
          <span>{{ t('objectTemplate.update') }}</span>
        </div>
        
        <div class="button-wrapper rel">
          <div
            :class="[isCreateButtonAvailableClassname, hasDesignTemplatesClassname, isTemplateRelatedOccupied]"
            @click="templateCreateProceed"
            class="button button--plain button--h-25"
          >
            <i v-if="!templateRelatedOccupied" class="fas fa-pencil-ruler"></i>
            <div
              v-else
              :class="templateRelatedOccupied.colorName"
              class="map-user map-user--small"
            >{{ templateRelatedOccupied.initials }}</div>
            <span class="ml--15">{{ t('objectTemplate.createShort') }}</span>
          </div>
          <dialog-inline
            v-if="isTemplateCreating"
            :position="'start'"
            :direction="'top'"
            :active="isTemplateCreating"
            @confirm="templateCreate"
            @cancel="templateCreateCancel"
          >
            <div class="field-row">
              <field-regular
                :label-inline="true"
                :field-item-classname="'max-width--150 mb--15'"
                :focused="1"
                v-model:model-value="templateCreatedName"
                @emit-action="templateCreate"
              ></field-regular>
            </div>
          </dialog-inline>
        </div>
			</div>
			<div
				@click="openDesignSettings"
				class="button button--plain button--h-25 bg--white color--black font-weight--500"
			>{{ t('quickEditBar.moreOptions') }}</div>
		</div>
	</div>
</template>

<script>
import AppearanceBorder from '@/components/editor-options/settings/sidebar/blocks/AppearanceBorder';
import EdgeLines from '@/components/editor-options/settings/sidebar/elements/EdgeLines';
import {computed, inject, reactive, ref} from 'vue';
import {useStore} from 'vuex';
import {SE_SYNC_TYPES} from '@/models/SE_SYNC_TYPES';
import { getEdgeChangedValues } from '@/modules/Edge';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import {useI18n} from 'vue-i18n';
import {TEMPLATE_TYPES} from "@/models/TEMPLATE_TYPES";
import DialogInline from "@/components/ui/dialog/DialogInline.vue";
import FieldRegular from "@/components/fields/FieldRegular.vue";
import useLoading from "@/utils/Loading";
import LoadingEl from "@/components/ui/loading/LoadingEl.vue";

export default {
	components: {
    LoadingEl,
    FieldRegular, DialogInline,
		AppearanceBorder,
		EdgeLines,
	},
	setup() {
		const {t} = useI18n();
		const store = useStore();
		const emitter = inject('emitter');
    // loading
    const {loading, loadingShow, loadingHide} = useLoading();
    
		const currentEdge = computed(() => store.getters["edges/getSelectedEdge"]);
		const currentEdgeOriginal = computed(() => store.getters["edges/getSelectedRawEdge"]);
		
		const EDGE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.EDGE.EDGE_DESIGN;
		async function openDesignSettings(){
			await store.dispatch("dashboardOptions/optionChoose", EDGE_DESIGN);
			await emitter.emit('closeQuickEditBarPanel');
		}
		
		// UTILS
		// DESIGN
		const edgeBorder = computed(() => currentEdge.value.border);
		const edgeWidth = computed({
			get(){return currentEdge.value.borderWidth},
			set(value){
				edgeDesignUpdate({borderWidth: value});
				edgeDesignSave({borderWidth: value});
			}
		});
		const edgeColor = computed({
			get(){return currentEdge.value.borderColor},
			set(value){
				edgeDesignUpdate({borderColor: value});
				edgeDesignSave({borderColor: value});
			}
		})
		const edgeStyle = computed({
			get(){return currentEdge.value.borderStyle},
			set(value){
				edgeDesignUpdate({borderStyle: value});
				edgeDesignSave({borderStyle: value});
			}
		})
		
		// EDGE LINE
		const edgeLineStart = computed({
			get(){return currentEdge.value.lineStart},
			set(value){
				edgeDesignUpdate({lineStart: value});
				edgeDesignSave({lineStart: value});
			}
		})
		const edgeLineEnd = computed({
			get(){return currentEdge.value.lineEnd},
			set(value){
				edgeDesignUpdate({lineEnd: value});
				edgeDesignSave({lineEnd: value});
			}
		})
		const edgeLineStartSize = computed({
			get(){return currentEdge.value.lineStartSize},
			set(value){
				edgeDesignUpdate({lineStartSize: value});
				edgeDesignSave({lineStartSize: value});
			}
		})
		const edgeLineEndSize = computed({
			get(){return currentEdge.value.lineEndSize},
			set(value){
				edgeDesignUpdate({lineEndSize: value});
				edgeDesignSave({lineEndSize: value});
			}
		})
		
		const edge = reactive({
			line: {
				start: edgeLineStart,
				startSize: edgeLineStartSize,
				end: edgeLineEnd,
				endSize: edgeLineEndSize
			},
			design: {
				width: edgeWidth,
				style: edgeStyle,
				border: {
					name: t('field.appearance.border'),
					value: edgeBorder,
					color: edgeColor
				},
				
			}
		});
		
		// TEMPLATE
		const clientRestrictions = computed(() => store.getters["clients/getRestrictions"]);
		const hasDesignTemplates = computed(() => clientRestrictions.value.hasDesignTemplates.value);
		const hasDesignTemplatesClassname = computed(() => hasDesignTemplates.value ? "" : "disabled");
		const isTemplateChangesTracked = computed(() => store.getters["edges/isTemplateChangesTracked"]);
		const isTemplateUpdateAvailable = computed(()=>
			isTemplateChangesTracked.value && currentEdge.value.template && currentEdge.value.template !== '0'
		);
		const isUpdateButtonAvailableClassname = computed(() => isTemplateUpdateAvailable.value ? "bg--pink" : "disabled bg--black");
    const isCreateButtonAvailableClassname = computed(() => isTemplateChangesTracked.value ? "bg--sea" : "disabled");
    
    // check if template is occupied
    const objectTemplates = computed(() => store.getters["templates/getObjectTemplates"]);
    const templateRelatedOccupied = computed(() => objectTemplates.value.find(template => template.id === parseInt(currentEdge.value.template))?.occupied);
    const isTemplateRelatedOccupied = computed(() => templateRelatedOccupied.value ? "disabled bg--black" : "");
		
		const edgeTemplates = computed(() => store.getters["design/getEdgesTemplates"])
		function getTemplate(templateId){
			let tmp = null;
			for(const [id, value] of Object.entries(edgeTemplates.value)){
				if(+id === +templateId) tmp = value
			}
			return tmp;
		}
		
		async function templateUpdate(){
			// save not null data from edge design object to template
			const updatedData = getEdgeChangedValues(currentEdgeOriginal.value.design);
			const currentTemplateData = JSON.parse(JSON.stringify(getTemplate(currentEdge.value.template)));
			const resultData = Object.assign(currentTemplateData, updatedData);
			const templateData = {
				id: parseInt(currentEdge.value.template),
				data: resultData
			}
			// update template in the store
			await store.dispatch('design/templateUpdate', templateData);
			// update template on the server
			await store.dispatch('design/templateSave', templateData);
			// update object template
			templateData.data.type = TEMPLATE_TYPES.EDGE;
			objectTemplateUpdateByData(templateData);
			objectTemplatesUpdatedApplyToEdges({templateId: templateData.id});
			
			const edgeDesign = await edgeNullify(currentEdge.value.id);
			
			await store.dispatch('edges/updateEdgeTemplate', {
				edgeData: edgeDesign,
				edgeId: currentEdge.value.id,
				type: SE_SYNC_TYPES.OBJECT_TEMPLATES_EDGE_UPDATED,
				template: templateData
			});
		}
    
    // TEMPLATE CREATE
    const isTemplateCreating = ref(false);
    const templateCreatedName = ref('');
    function templateCreateProceed(){
      isTemplateCreating.value = true;
    }
    function templateCreateCancel(){
      isTemplateCreating.value = false;
      templateCreatedName.value = '';
    }
    async function templateCreate(){
      if(!templateCreatedName.value) return;
      const templateObject = {name: templateCreatedName.value};
      const edgeId = currentEdgeOriginal.value.id;
      Object.assign(templateObject, {type: 'edge'});
      const template = await store.dispatch('design/templateCreate', {templateObject});

      loadingShow();
      await store.dispatch('edges/populateCreatedEdgeTemplate', {edgeId, template});

      await store.dispatch('edges/saveEdgeDesignByTemplate', {edgeId, edgeData: currentEdgeOriginal.value.design});
      await store.dispatch('design/templateSave', {id: template.id});

      await store.dispatch('templates/objectTemplateCreate', {id: template.id});
      loadingHide();
      templateCreateCancel();
      // SE send event
      const editsData = {
        type: SE_SYNC_TYPES.EDGE_TEMPLATE_CREATED,
        data: {edgeId, template}
      }
      await store.dispatch('simultaneousEditing/syncEdits', editsData);
    }
		
		// STORE ACTIONS
		function edgeNullify(edgeId){
			return store.dispatch('edges/nullifyEdge', edgeId);
		}
		function edgeDesignUpdate(edgeData){
			const id = currentEdge.value.id;
			store.dispatch('edges/design/edgeDesignUpdate', {id, edgeData});
		}
		function edgeDesignSave(edgeData){
			const id = currentEdge.value.id;
			store.dispatch('edges/design/edgeDesignSave', {id, edgeData})
		}
		function objectTemplateUpdateByData({id, data}){
			store.dispatch('templates/objectTemplateUpdateByData', {id, data})
		}
		function objectTemplatesUpdatedApplyToEdges(templateId){
			store.dispatch('edges/applyUpdatedObjectTemplatesToEdges', templateId);
		}
		
		return {
			t,
			loading,
			edge,
			isUpdateButtonAvailableClassname,
			hasDesignTemplatesClassname,
			templateUpdate,
			openDesignSettings,
			templateRelatedOccupied,
      isTemplateRelatedOccupied,
      isCreateButtonAvailableClassname,
      templateCreateProceed,
      isTemplateCreating,
      templateCreate,
      templateCreateCancel,
      templateCreatedName
		}
	
	}
}
</script>

<style lang="scss">

</style>
